// ============================================================
// ChessBoard — React wrapper around chessground
// Handles FEN sync, arrows, highlights, orientation, last move
// ============================================================

import { useRef, useEffect, useCallback } from "react"
import { Chessground } from "chessground"
import type { Api } from "chessground/api"
import type { Config } from "chessground/config"
import type { DrawShape } from "chessground/draw"
import type { Key, Color } from "chessground/types"
import type { BoardArrow } from "@shared/types"
import styles from "./ChessBoard.module.css"

// Import chessground CSS
import "chessground/assets/chessground.base.css"
import "chessground/assets/chessground.brown.css"
import "chessground/assets/chessground.cburnett.css"

interface ChessBoardProps {
  fen: string
  orientation: Color
  lastMove?: [string, string]
  arrows?: BoardArrow[]
  highlights?: string[]
  animationDuration?: number
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
}

// Map our arrow color types to chessground brush colors
function arrowColorToBrush(color: BoardArrow["color"]): string {
  switch (color) {
    case "played":
      return "red"
    case "best":
      return "green"
    case "threat":
      return "yellow"
    default:
      return "red"
  }
}

function buildAutoShapes(
  arrows: BoardArrow[],
  highlights: string[]
): DrawShape[] {
  const shapes: DrawShape[] = []

  for (const arrow of arrows) {
    shapes.push({
      orig: arrow.from as Key,
      dest: arrow.to as Key,
      brush: arrowColorToBrush(arrow.color),
    })
  }

  for (const square of highlights) {
    shapes.push({
      orig: square as Key,
      brush: "yellow",
    })
  }

  return shapes
}

export const ChessBoard = ({
  fen,
  orientation,
  lastMove,
  arrows = [],
  highlights = [],
  animationDuration = 150,
  onSwipeLeft,
  onSwipeRight,
}: ChessBoardProps) => {
  const boardRef = useRef<HTMLDivElement>(null)
  const apiRef = useRef<Api | null>(null)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)

  // Initialize chessground
  useEffect(() => {
    if (!boardRef.current) return

    const config: Config = {
      fen,
      orientation,
      viewOnly: true,
      coordinates: false,
      animation: {
        enabled: true,
        duration: animationDuration,
      },
      highlight: {
        lastMove: true,
        check: true,
      },
      movable: {
        free: false,
        color: undefined,
      },
      draggable: {
        enabled: false,
      },
      selectable: {
        enabled: false,
      },
      drawable: {
        enabled: false,
        visible: true,
        autoShapes: buildAutoShapes(arrows, highlights),
      },
      lastMove: lastMove as [Key, Key] | undefined,
    }

    apiRef.current = Chessground(boardRef.current, config)

    return () => {
      apiRef.current?.destroy()
      apiRef.current = null
    }
    // Init once on mount -- subsequent updates handled by separate effects
  }, [])

  // Update FEN and last move
  useEffect(() => {
    if (!apiRef.current) return
    apiRef.current.set({
      fen,
      lastMove: lastMove as [Key, Key] | undefined,
      animation: {
        enabled: true,
        duration: animationDuration,
      },
    })
  }, [fen, lastMove, animationDuration])

  // Update orientation
  useEffect(() => {
    if (!apiRef.current) return
    apiRef.current.set({ orientation })
  }, [orientation])

  // Update arrows and highlights
  useEffect(() => {
    if (!apiRef.current) return
    apiRef.current.setAutoShapes(buildAutoShapes(arrows, highlights))
  }, [arrows, highlights])

  // Touch/swipe handling for mobile navigation
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0]
    if (touch) {
      touchStartRef.current = { x: touch.clientX, y: touch.clientY }
    }
  }, [])

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartRef.current) return
      const touch = e.changedTouches[0]
      if (!touch) return

      const deltaX = touch.clientX - touchStartRef.current.x
      const deltaY = touch.clientY - touchStartRef.current.y
      const minSwipeDistance = 30

      // Only register horizontal swipes (not vertical scrolling)
      if (
        Math.abs(deltaX) > minSwipeDistance &&
        Math.abs(deltaX) > Math.abs(deltaY)
      ) {
        if (deltaX < 0 && onSwipeLeft) {
          // Swipe left = forward
          onSwipeLeft()
        } else if (deltaX > 0 && onSwipeRight) {
          // Swipe right = backward
          onSwipeRight()
        }

        // Haptic feedback if available
        if (navigator.vibrate) {
          navigator.vibrate(10)
        }
      }

      touchStartRef.current = null
    },
    [onSwipeLeft, onSwipeRight]
  )

  return (
    <div
      className={styles.boardWrapper}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      role="img"
      aria-label="Chess board showing current position"
    >
      <div ref={boardRef} className={styles.board} />
    </div>
  )
}
